import React, { useEffect, useState } from 'react';
import { fetchReadyForBgcCandidates } from '../../services/user/api';
import { Search, ArrowLeft } from 'lucide-react';
import './PendingCaseSubmission.css';
import "../../styles/user/readyForBgc.css";
import { FaEye } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const ReadyForBgcPage = ({ onViewReportTwo }) => {
  const navigate = useNavigate();
    const [candidates, setCandidates] = useState([]);
    const [fetchError, setFetchError] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [entriesPerPage, setEntriesPerPage] = useState(10);
    const [sortField, setSortField] = useState('createddate');
    const [sortOrder, setSortOrder] = useState('desc');
    const [isAsc, setIsAsc] = useState(false);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(search.trim());
            setCurrentPage(0);
        }, 500);
        return () => clearTimeout(handler);
    }, [search]);

    useEffect(() => {
        loadCandidates();
    }, [currentPage, entriesPerPage, debouncedSearch, sortField, sortOrder]);

    const loadCandidates = async () => {
    try {
        setLoading(true);
        setFetchError(null);

        const response = await fetchReadyForBgcCandidates(
            currentPage, 
            entriesPerPage, 
            debouncedSearch, 
            sortField || 'createddate', 
            sortOrder
        );
        
        // 💡 Check directly for response.data or response.data.content
        if (response?.data && Array.isArray(response.data.content)) {
            const data = response.data; // response.data IS the Page object itself
            
            setCandidates(data.content);
            setTotalElements(data.totalElements || 0);
        } else {
            setFetchError('No data returned from backend.');
            setCandidates([]);
            setTotalElements(0);
        }
    } catch (error) {
        console.error('API Error:', error);
        setFetchError('An error occurred while fetching data.');
        setCandidates([]);
        setTotalElements(0);
    } finally {
        setLoading(false);
    }
};

    const handleSort = (column) => {
        if (sortField === column) {
            const newIsAsc = !isAsc;
            setIsAsc(newIsAsc);
            setSortOrder(newIsAsc ? "asc" : "desc");
        } else {
            setSortField(column);
            setIsAsc(true);
            setSortOrder("asc");
        }
        setCurrentPage(0);
    };

    const renderSortArrow = (field) => {
        const isActive = sortField === field;
        return (
            <span className={`sort-icons ${isActive ? "sort-active" : ""}`}>
                {!isActive ? "▲▼" : sortOrder === "asc" ? "▲" : "▼"}
            </span>
        );
    };

    const formatDate = (date) => {
        if (!date) return "---";
        const d = new Date(date);
        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();
        return `${day}-${month}-${year}`;
    };

    const totalPages = Math.ceil(totalElements / entriesPerPage);


   return (
  <>
    {/* Back Button */}
    <div className="ready-bgc-back-wrapper">
      <button
        type="button"
        className="ready-bgc-back-button"
        onClick={() => navigate(-1)}
      >
        <ArrowLeft size={18} />
        <span>Back</span>
      </button>
    </div>

    <p className="client-header">Ready for BGC Candidates</p>

    <div className="client-wrapper">
        <div className="clinet-table-controls">
          <div className="entries-select">
            Show{" "}
            <select
              value={entriesPerPage}
              onChange={(e) => {
                setEntriesPerPage(Number(e.target.value));
                setCurrentPage(0);
              }}
            >
              <option value="10">10</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>{" "}
            entries
          </div>

          <div className="client-search-box">
            <div className="client-search-input-wrapper">
              <input
                id="client-search-input"
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(0);
                }}
                placeholder="Search by any field..."
              />
              <Search className="client-search-icon" size={18} />
            </div>
          </div>
        </div>

        <div className="clinet-table-container">
          <table className="candidates-clinet-table">
            <colgroup>
              <col style={{ width: "130px" }} />
              <col style={{ width: "180px" }} />
              <col style={{ width: "220px" }} />
              <col style={{ width: "150px" }} />
              <col style={{ width: "120px" }} />
              <col style={{ width: "140px" }} />
              <col style={{ width: "130px" }} />
              <col style={{ width: "150px" }} />
            </colgroup>
            <thead>
              <tr>
                <th onClick={() => handleSort("candidateid")}>
                  Candidate ID {renderSortArrow("candidateid")}
                </th>
                <th onClick={() => handleSort("name")}>
                  Name {renderSortArrow("name")}
                </th>
                <th onClick={() => handleSort("email")}>
                  Email {renderSortArrow("email")}
                </th>
                <th onClick={() => handleSort("mobileNumber")}>
                  Mobile Number {renderSortArrow("mobileNumber")}
                </th>
                <th onClick={() => handleSort("dateOfBirth")}>
                  DOB {renderSortArrow("dateOfBirth")}
                </th>
                <th onClick={() => handleSort("caseInitiatedDateOnly")}>
                  Created Date {renderSortArrow("caseInitiatedDateOnly")}
                </th>
                <th onClick={() => handleSort("caseInitiatedTimeOnly")}>
                  Created Time {renderSortArrow("caseInitiatedTimeOnly")}
                </th>
                <th>Status</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="loading">
                    Loading Candidates...
                  </td>
                </tr>
              ) : fetchError ? (
                <tr>
                  <td colSpan="8" className="no-data">
                    {fetchError}
                  </td>
                </tr>
              ) : candidates.length > 0 ? (
                candidates.map((candidate) => (
                  <tr key={candidate.candidateid}>
                    <td>{candidate.candidateid}</td>
                    <td>{candidate.fullname}</td>
                    <td>{candidate.email}</td>
                    <td>{candidate.mobileNumber}</td>
                    <td>{candidate.dateOfBirth}</td>
                    <td>{formatDate(candidate.caseInitiatedDateOnly)}</td>
                    <td>{candidate.caseInitiatedTimeOnly || "---"}</td>
                    <td>
                      <span>
                        READY_FOR_BGC
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="no-data">
                    No matching candidates found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="pagination">
          {totalElements > 0 && (
            <div className="pagination-summary">
              Showing {currentPage * entriesPerPage + 1} to{" "}
              {Math.min((currentPage + 1) * entriesPerPage, totalElements)} of{" "}
              {totalElements} entries
            </div>
          )}

          {totalElements > 0 && (
            <div className="pagination-buttons-user">
              <button
                className="page-btn"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
                disabled={currentPage === 0}
              >
                ←
              </button>

              <button
                className="page-btn active"
                onClick={() => setCurrentPage(currentPage)}
              >
                {currentPage + 1}
              </button>

              {currentPage + 1 < totalPages && (
                <button
                  className="page-btn"
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  {currentPage + 2}
                </button>
              )}

              <button
                className="page-btn"
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1))
                }
                disabled={currentPage === totalPages - 1}
              >
                →
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ReadyForBgcPage;